/**
 * Workshop settings page — settings modal with all 9 tabbed sections.
 *
 * Click a tab button to open the modal to that section.
 * The modal is a grouped-sidebar layout with dummy data in every section.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SettingsModal } from "@/workshop/components/settings/settings-modal";
import type { SettingsTab } from "@/workshop/components/settings/settings-modal";
import { ProfileSection } from "@/workshop/components/settings/profile-section";
import { BillingSection } from "@/workshop/components/settings/billing-section";
import { ConnectedAccountsSection } from "@/workshop/components/settings/connected-accounts-section";
import { AgentsSection } from "@/workshop/components/settings/agents-section";
import { ProjectsSection } from "@/workshop/components/settings/projects-section";
import { AppearanceSection } from "@/workshop/components/settings/appearance-section";
import { NotificationsSection } from "@/workshop/components/settings/notifications-section";
import { ContactSection } from "@/workshop/components/settings/contact-section";
import { HelpSection } from "@/workshop/components/settings/help-section";
import { CompanionSection } from "@/workshop/components/settings/companion-section";

/* ---------- Tab content renderer ---------- */

function renderSettingsContent(tab: SettingsTab): React.ReactNode {
  switch (tab) {
    case "profile":
      return <ProfileSection />;
    case "billing":
      return <BillingSection />;
    case "connected-accounts":
      return <ConnectedAccountsSection />;
    case "companion":
      return <CompanionSection />;
    case "agents":
      return <AgentsSection />;
    case "projects":
      return <ProjectsSection />;
    case "appearance":
      return <AppearanceSection />;
    case "notifications":
      return <NotificationsSection />;
    case "contact":
      return <ContactSection />;
    case "help":
      return <HelpSection />;
  }
}

/* ---------- Tab presets ---------- */

const ALL_TABS: SettingsTab[] = [
  "profile",
  "billing",
  "connected-accounts",
  "companion",
  "agents",
  "projects",
  "appearance",
  "notifications",
  "contact",
  "help",
];

/* ---------- Main page ---------- */

export function SettingsPage() {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");

  function openTo(tab: SettingsTab) {
    setActiveTab(tab);
    setIsOpen(true);
  }

  return (
    <div className="p-6" data-testid="workshop-settings-page">
      <h1 className="mb-2 text-lg font-semibold text-primary">
        Settings Modal
      </h1>
      <p className="mb-4 text-sm text-secondary">
        Click a tab below to open the settings modal to that section.
      </p>

      <div className="flex flex-wrap gap-2">
        {ALL_TABS.map((tab) => (
          <Button
            key={tab}
            variant="secondary"
            size="sm"
            onClick={() => openTo(tab)}
            data-testid={`open-settings-${tab}`}
          >
            {tab.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </Button>
        ))}
      </div>

      <SettingsModal
        open={isOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onClose={() => setIsOpen(false)}
        renderContent={renderSettingsContent}
      />
    </div>
  );
}
